/**
 * Firm Slot Dashboard — a compact strip on /app/clients that shows per-client
 * bank-account + team-member usage, highlighting workspaces approaching their
 * caps so the firm can top them up proactively.
 *
 * Status thresholds:
 *   full (red)  — current >= cap
 *   warn (amber) — current >= 80% of cap
 *   ok   (gray)  — plenty of headroom
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, Landmark, Users, ExternalLink, Loader2, CheckCircle2 } from "lucide-react";

function Pill({ label, used, cap, status, icon: Icon }) {
  const palette = status === "full"
    ? { bg: "#FEE2E2", fg: "#991B1B", br: "#F87171" }
    : status === "warn"
      ? { bg: "#FEF3C7", fg: "#92400E", br: "#F59E0B" }
      : { bg: "#F0FDF4", fg: "#065F46", br: "#86EFAC" };
  return (
    <div
      className="inline-flex items-center gap-1 px-2 py-1 border text-xs font-mono-fin"
      style={{ background: palette.bg, color: palette.fg, borderColor: palette.br }}
    >
      <Icon size={12} />
      {used}/{cap}
    </div>
  );
}

export default function FirmSlotDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/firm/slot-dashboard")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="ll-progress mb-6" data-testid="slot-dashboard-loading" />;
  if (!data || !data.clients?.length) return null;

  const { summary, clients } = data;
  const alerting = clients.filter((c) => c.status !== "ok");
  const needsAttention = alerting.length > 0;

  return (
    <div
      className="ll-card-flat p-5 mb-6"
      style={{
        background: needsAttention ? "#FFFBEB" : "#F0FDF4",
        borderColor: needsAttention ? "#F59E0B" : "#86EFAC",
      }}
      data-testid="firm-slot-dashboard"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          {needsAttention
            ? <AlertTriangle size={16} className="text-[#B45309]" />
            : <CheckCircle2 size={16} className="text-[#059669]" />}
          <div>
            <div className="font-display font-bold text-base">
              {needsAttention
                ? `${alerting.length} client${alerting.length === 1 ? "" : "s"} need${alerting.length === 1 ? "s" : ""} attention`
                : "All clients comfortably below their caps"}
            </div>
            <div className="text-xs text-[var(--ll-ink-soft)]">
              {summary.at_cap} at cap · {summary.near_cap} nearing cap · {summary.total - summary.at_cap - summary.near_cap} ok
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="slot-dashboard-expand"
          className="text-xs px-3 py-1.5 border hover:bg-white"
          style={{ borderColor: "var(--ll-border)" }}
        >
          {expanded ? "Hide details" : "View all clients"}
        </button>
      </div>

      {/* Collapsed state: show only alerting rows (up to 5) */}
      {!expanded && alerting.length > 0 && (
        <div className="space-y-1" data-testid="slot-dashboard-alerts">
          {alerting.slice(0, 5).map((c) => (
            <SlotRow key={c.tenant_id} client={c} />
          ))}
          {alerting.length > 5 && (
            <div className="text-xs text-[var(--ll-ink-soft)] pt-1">
              + {alerting.length - 5} more — click &ldquo;View all&rdquo; to see every client
            </div>
          )}
        </div>
      )}

      {expanded && (
        <div className="space-y-1" data-testid="slot-dashboard-all">
          {clients.map((c) => <SlotRow key={c.tenant_id} client={c} />)}
        </div>
      )}
    </div>
  );
}

function SlotRow({ client }) {
  return (
    <div
      className="flex items-center justify-between gap-3 bg-white border px-3 py-2"
      style={{ borderColor: "var(--ll-border)" }}
      data-testid={`slot-row-${client.tenant_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{client.name}</div>
        <div className="text-[11px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
          {client.plan} plan
        </div>
      </div>
      <Pill label="banks" icon={Landmark} used={client.bank_accounts.used} cap={client.bank_accounts.cap} status={client.bank_accounts.status} />
      <Pill label="team" icon={Users} used={client.team_members.used} cap={client.team_members.cap} status={client.team_members.status} />
      <Link
        to={`/app/clients?open=${client.tenant_id}`}
        data-testid={`slot-row-open-${client.tenant_id}`}
        className="text-xs text-[var(--ll-ink)] underline hover:no-underline flex items-center gap-1"
      >
        Top up <ExternalLink size={10} />
      </Link>
    </div>
  );
}
